// @ts-nocheck
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useStore } from "@tanstack/react-form";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/shared/NumericInput";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import {
  Save,
  Loader2,
  AlertTriangle,
  ShieldAlert,
  GitBranch,
  CheckCircle2,
  Wrench,
  FileWarning,
  HelpCircle,
} from "lucide-react";
import { cn, formatNumber } from "@/lib/utils";
import { joinFieldErrors } from "@/lib/form/FormFieldError";
import { ncrSchema } from "@/lib/schemas/qc";
import {
  StageDefectSelectorTable,
  normalizeDefectTallyItems,
  type DefectTallyItem,
} from "@/components/quality/StageDefectSelectorTable";
import { useNcrDispositions } from "@/hooks/useNcrDispositions";
import { QcCategorizedMediaSection } from "@/components/quality/QcCategorizedMediaSection";
import { detailFieldSize } from "@/components/shared/DetailField";

export interface NCRFormProps {
  defaultValues?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}


const ISHIKAWA_6M = [
  { code: "MAN", label: "1. Con người" },
  { code: "MACHINE", label: "2. Máy móc" },
  { code: "MATERIAL", label: "3. Vật tư" },
  { code: "METHOD", label: "4. Phương pháp" },
  { code: "MEASUREMENT", label: "5. Đo lường" },
  { code: "ENVIRONMENT", label: "6. Môi trường" },
];

// Ô form rộng theo loại dữ liệu như màn chi tiết (lệnh SX, sản phẩm… 2 ô; ghi chú, nguyên nhân cả hàng)
const F = ({ field, label, required, children }: any) => (
  <div
    data-detail-field
    className={`space-y-1.5 flex flex-col min-w-0 ${typeof label === "string" ? { short: "", medium: "col-span-2", full: "col-span-full" }[detailFieldSize(label)] : ""}`}
  >
    <Label
      htmlFor={field?.name}
      className="text-xs font-semibold text-slate-700 whitespace-normal"
    >
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
    {field?.state?.meta?.errors && field.state.meta.errors.length > 0 ? (
      <p className="text-[11px] font-medium text-destructive mt-1">
        {joinFieldErrors(field.state.meta.errors)}
      </p>
    ) : null}
  </div>
);

const DETECT_POINTS = ["IQC", "IPQC", "FQC"];

/** Phiếu (mới / đang sửa) → giá trị ô "Nguồn phát hiện" */
function toDetectedAt(v: any): string {
  const source = String(v?.source || "").toUpperCase();
  const sourceType = String(v?.sourceType || "").toUpperCase();
  if (source === "CUSTOMER") return "CUSTOMER";
  if (DETECT_POINTS.includes(sourceType)) return sourceType;
  if (DETECT_POINTS.includes(source)) return source; // lập từ phiếu QC: ?sourceType=IQC
  if (source === "SUPPLIER" || source === "INTERNAL") return source;
  return "IPQC";
}

/** Giá trị ô "Nguồn phát hiện" → 2 cột lưu: source (Nội bộ / Khách hàng / NCC) + sourceType (nơi phát hiện) */
function fromDetectedAt(key: string): { source: "INTERNAL" | "CUSTOMER" | "SUPPLIER"; sourceType: string | null } {
  switch (key) {
    case "IQC":
      return { source: "SUPPLIER", sourceType: "IQC" };
    case "IPQC":
    case "FQC":
      return { source: "INTERNAL", sourceType: key };
    case "CUSTOMER":
      return { source: "CUSTOMER", sourceType: "CUSTOMER_CLAIM" };
    case "SUPPLIER":
      return { source: "SUPPLIER", sourceType: null };
    default:
      return { source: "INTERNAL", sourceType: null };
  }
}

export default function NCRForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu Phiếu NCR",
}: NCRFormProps) {
  const initialWhy = defaultValues?.whyAnalysis || {};
  const [why1, setWhy1] = useState<string>(initialWhy.why1 || "");

  const { options: dispositionOptions } = useNcrDispositions();
  const [why2, setWhy2] = useState<string>(initialWhy.why2 || "");
  const [why3, setWhy3] = useState<string>(initialWhy.why3 || "");
  const [why4, setWhy4] = useState<string>(initialWhy.why4 || "");
  const [why5, setWhy5] = useState<string>(initialWhy.why5 || "");

  const [defectDetails, setDefectDetails] = useState<DefectTallyItem[]>(() =>
    normalizeDefectTallyItems(defaultValues?.defectDetails)
  );
  const [attachments, setAttachments] = useState<any>(
    () => defaultValues?.attachments || []
  );

  const initialCostBreakdown =
    defaultValues?.costBreakdown && typeof defaultValues.costBreakdown === "object"
      ? defaultValues.costBreakdown
      : {};
  const [scrapCost, setScrapCost] = useState<number>(
    Number(initialCostBreakdown.scrapCost || 0)
  );
  const [reworkCost, setReworkCost] = useState<number>(
    Number(initialCostBreakdown.reworkCost || 0)
  );
  const [reprintCost, setReprintCost] = useState<number>(
    Number(initialCostBreakdown.reprintCost || 0)
  );
  const [costNotes, setCostNotes] = useState<string>(
    initialCostBreakdown.notes || ""
  );

  const failedCriteria: any[] = Array.isArray(defaultValues?.failedCriteria)
    ? defaultValues.failedCriteria
    : [];

  const form = useForm({
    defaultValues: {
      // "Nguồn phát hiện" là 1 ô chọn nhưng lưu 2 cột: nơi phát hiện (sourceType) + nguồn gốc (source: Nội bộ / Khách hàng / NCC)
      detectedAt: toDetectedAt(defaultValues),
      severity: defaultValues?.severity ?? "HIGH",
      issueSummary: defaultValues?.issueSummary ?? "",
      relatedType: defaultValues?.relatedType ?? "",
      relatedId: defaultValues?.relatedId ?? "",
      qcMaterialId: defaultValues?.qcMaterialId ?? "",
      qcProcessId: defaultValues?.qcProcessId ?? "",
      qcFinalId: defaultValues?.qcFinalId ?? "",
      productionOrderId: defaultValues?.productionOrderId ?? "",
      routingStepId: defaultValues?.routingStepId ?? "",
      processStage: defaultValues?.processStage ?? "",
      machineId: defaultValues?.machineId ?? "",
      materialId: defaultValues?.materialId ?? "",
      vendorId: defaultValues?.vendorId ?? "",
      productId: defaultValues?.productId ?? "",
      customerId: defaultValues?.customerId ?? "",
      batchNumber: defaultValues?.batchNumber ?? "",
      lotSize: defaultValues?.lotSize ?? 0,
      checkedQty: defaultValues?.checkedQty ?? defaultValues?.sampleSize ?? 0,
      sampleSize: defaultValues?.sampleSize ?? 0,
      okQty:
        defaultValues?.okQty ??
        Math.max(
          0,
          (Number(defaultValues?.checkedQty ?? defaultValues?.sampleSize) || 0) -
            (Number(defaultValues?.defectQty) || 0)
        ),
      defectQty: defaultValues?.defectQty ?? 0,
      unit: defaultValues?.unit ?? "Tờ",
      disposition: defaultValues?.disposition ?? "REWORK",
      defectCategory6M: defaultValues?.defectCategory6M ?? "MACHINE",
      responsibleDept: defaultValues?.responsibleDept ?? "SAN_XUAT",
      ownerId: defaultValues?.ownerId ?? "",
      rootCause: defaultValues?.rootCause ?? "",
      containmentAction: defaultValues?.containmentAction ?? "",
      correctiveAction: defaultValues?.correctiveAction ?? "",
      preventiveAction: defaultValues?.preventiveAction ?? "",
      resolutionResult: defaultValues?.resolutionResult ?? "",
      capaPlan: defaultValues?.capaPlan ?? "",
      costImpact: defaultValues?.costImpact ?? 0,
      status: defaultValues?.status ?? "OPEN",
    },
    validators: {
      onChange: ncrSchema as any,
    },
    onSubmit: async ({ value }) => {
      const combinedWhyRootCause =
        value.rootCause?.trim() ||
        [why1, why2, why3, why4, why5].filter(Boolean).join(" → ");
      // D3 / D5 / D7 lưu cột riêng; Kế hoạch CAPA chỉ là ô người dùng nhập (không tự gộp — trước gộp 1 lần rồi sửa D5 / D7 không lưu)

      const breakdownSum =
        (Number(scrapCost) || 0) +
        (Number(reworkCost) || 0) +
        (Number(reprintCost) || 0);
      const finalCostImpact =
        breakdownSum > 0 ? breakdownSum : Number(value.costImpact) || 0;

      const checked = Number(value.checkedQty) || Number(value.sampleSize) || 0;
      const ng = Number(value.defectQty) || 0;
      const ok =
        value.okQty !== undefined && value.okQty !== null
          ? Number(value.okQty)
          : Math.max(0, checked - ng);

      const { detectedAt, ...rest } = value as any;
      await onSubmit({
        ...rest,
        ...fromDetectedAt(detectedAt),
        lotSize: Number(value.lotSize) || 0,
        checkedQty: checked,
        sampleSize: Number(value.sampleSize) || checked,
        okQty: ok,
        defectQty: ng,
        costImpact: finalCostImpact,
        costBreakdown: {
          scrapCost: Number(scrapCost) || 0,
          reworkCost: Number(reworkCost) || 0,
          reprintCost: Number(reprintCost) || 0,
          notes: costNotes || "",
        },
        resolutionResult: value.resolutionResult || "",
        failedCriteria,
        defectDetails,
        attachments,
        whyAnalysis: { why1, why2, why3, why4, why5 },
        rootCause: combinedWhyRootCause,
        capaPlan: value.capaPlan?.trim() || "",
      });
    },
  });

  const selected6M = useStore(
    form.store,
    (s: any) => s.values.defectCategory6M
  );
  const selectedDisposition = useStore(
    form.store,
    (s: any) => s.values.disposition
  );
  const selectedSource = useStore(form.store, (s: any) => s.values.detectedAt);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <div className="space-y-4 w-full mx-auto pb-8">
      <form
        id="ncr-canonical-form"
        onSubmit={handleFormSubmit}
        className="space-y-4"
      >
        {/* ═══ CARD 1: ĐỊNH DANH SỰ CỐ & TRUY VẾT CHỨNG TỪ GỐC (TRACEABILITY FKs) ═══ */}
        <Card className="border-none shadow-2xs ring-1 ring-slate-200">
          <CardHeader className="bg-rose-50/60 py-3 border-b border-rose-200/80">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-black uppercase tracking-wider text-rose-900 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-rose-600" /> D1–D2. Mô tả sự không phù hợp
              </CardTitle>
              {(defaultValues?.qcMaterialId ||
                defaultValues?.qcProcessId ||
                defaultValues?.qcFinalId) && (
                <Badge className="bg-rose-600 text-white font-mono text-xs">
                  Kế thừa trực tiếp từ Biên bản KCS:{" "}
                  {defaultValues?.relatedCode || defaultValues?.relatedId}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <form.Field name="detectedAt">
                {(field: any) => (
                  <F field={field} label="Nguồn phát hiện" required>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v)}
                    >
                      <SelectTrigger className="h-9 text-xs font-semibold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IQC">
                          IQC
                        </SelectItem>
                        <SelectItem value="IPQC">
                          IPQC
                        </SelectItem>
                        <SelectItem value="FQC">
                          FQC
                        </SelectItem>
                        <SelectItem value="CUSTOMER">
                          Khách hàng
                        </SelectItem>
                        <SelectItem value="SUPPLIER">
                          NCC
                        </SelectItem>
                        <SelectItem value="INTERNAL">
                          Nội bộ
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                )}
              </form.Field>

              <form.Field name="severity">
                {(field: any) => (
                  <F field={field} label="Mức độ" required>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v)}
                    >
                      <SelectTrigger className="h-9 text-xs font-bold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CRITICAL">
                          Nghiêm trọng
                        </SelectItem>
                        <SelectItem value="HIGH">
                          Cao
                        </SelectItem>
                        <SelectItem value="MEDIUM">
                          Trung bình
                        </SelectItem>
                        <SelectItem value="LOW">
                          Thấp
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                )}
              </form.Field>

              <form.Field name="status">
                {(field: any) => (
                  <F field={field} label="Trạng thái">
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v)}
                    >
                      <SelectTrigger className="h-9 text-xs font-semibold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPEN">
                          Mới mở
                        </SelectItem>
                        <SelectItem value="INVESTIGATING">
                          Đang điều tra
                        </SelectItem>
                        <SelectItem value="CORRECTIVE_ACTION">
                          Đang khắc phục
                        </SelectItem>
                        <SelectItem value="VERIFICATION">
                          Chờ thẩm tra
                        </SelectItem>
                        <SelectItem value="CLOSED">
                          Đã đóng
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                )}
              </form.Field>

              <form.Field name="batchNumber">
                {(field: any) => (
                  <F field={field} label="Số lô">
                    <Input
                      className="h-9 font-mono text-xs"
                      value={field.state.value ?? ""}
                      placeholder="VD: LOT-PO2610..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            {/* Relational FKs Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-slate-100">
              <form.Field name="productionOrderId">
                {(field: any) => (
                  <F field={field} label="Lệnh SX">
                    <SharedCombobox
                      type="productionOrder"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn LSX liên quan..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="processStage">
                {(field: any) => (
                  <F field={field} label="Công đoạn">
                    <Select
                      value={field.state.value || "NONE"}
                      onValueChange={(v) =>
                        field.handleChange(v === "NONE" ? "" : v)
                      }
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Chọn công đoạn..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">-- Không chọn --</SelectItem>
                        <SelectItem value="CUTTING">
                          1. Cắt giấy / xả cuộn
                        </SelectItem>
                        <SelectItem value="PRINTING">
                          2. In offset
                        </SelectItem>
                        <SelectItem value="LAMINATING">
                          3. Cán màng
                        </SelectItem>
                        <SelectItem value="COATING">
                          4. Tráng phủ UV
                        </SelectItem>
                        <SelectItem value="DIE_CUTTING">
                          5. Bế / ép kim / bồi sóng
                        </SelectItem>
                        <SelectItem value="GLUING">
                          6. Dán hộp
                        </SelectItem>
                        <SelectItem value="PACKING">
                          7. Đóng gói
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                )}
              </form.Field>

              <form.Field name="machineId">
                {(field: any) => (
                  <F field={field} label="Máy">
                    <SharedCombobox
                      type="machine"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn máy..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="productId">
                {(field: any) => (
                  <F field={field} label="Sản phẩm">
                    <SharedCombobox
                      type="product"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn sản phẩm..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="materialId">
                {(field: any) => (
                  <F field={field} label="Vật tư">
                    <SharedCombobox
                      type="material"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn vật tư..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="vendorId">
                {(field: any) => (
                  <F field={field} label="Nhà cung cấp">
                    <SharedCombobox
                      type="vendor"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn nhà cung cấp..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="customerId">
                {(field: any) => (
                  <F field={field} label="Khách hàng">
                    <SharedCombobox
                      type="customer"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn khách hàng..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="relatedId">
                {(field: any) => (
                  <F field={field} label="Chứng từ gốc">
                    <Input
                      className="h-9 font-mono text-xs"
                      value={field.state.value ?? ""}
                      placeholder="Mã phiếu IQC / IPQC / FQC / PO..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            {/* Quantities & Summary Row (Lot Size, Checked/Sample, OK, NG, Unit) */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2 border-t border-slate-100">
              <form.Field name="lotSize">
                {(field: any) => (
                  <F field={field} label="Cỡ lô">
                    <NumericInput
                      className="h-9 font-mono font-bold bg-slate-50"
                      value={field.state.value ?? 0}
                      onChange={(rawVal) =>
                        field.handleChange(Number(rawVal) || 0)
                      }
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="checkedQty">
                {(field: any) => (
                  <F field={field} label="Cỡ mẫu">
                    <NumericInput
                      className="h-9 font-mono font-bold text-blue-800 border-blue-200 bg-blue-50/40"
                      value={field.state.value ?? 0}
                      onChange={(rawVal) => {
                        const val = Number(rawVal) || 0;
                        field.handleChange(val);
                        form.setFieldValue("sampleSize", val);
                        const curNg = Number(form.getFieldValue("defectQty") || 0);
                        form.setFieldValue("okQty", Math.max(0, val - curNg));
                      }}
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="okQty">
                {(field: any) => (
                  <F field={field} label="SL đạt">
                    <NumericInput
                      className="h-9 font-mono font-bold text-emerald-800 border-emerald-200 bg-emerald-50/40"
                      value={field.state.value ?? 0}
                      onChange={(rawVal) =>
                        field.handleChange(Number(rawVal) || 0)
                      }
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="defectQty">
                {(field: any) => (
                  <F
                    field={field}
                    label="SL lỗi"
                    required
                  >
                    <NumericInput
                      className="h-9 font-mono font-bold text-rose-700 border-rose-300 bg-rose-50/40"
                      value={field.state.value ?? 0}
                      onChange={(rawVal) => {
                        const val = Number(rawVal) || 0;
                        field.handleChange(val);
                        const curChecked =
                          Number(form.getFieldValue("checkedQty")) ||
                          Number(form.getFieldValue("sampleSize")) ||
                          0;
                        if (curChecked > 0) {
                          form.setFieldValue("okQty", Math.max(0, curChecked - val));
                        }
                      }}
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="unit">
                {(field: any) => (
                  <F field={field} label="ĐVT">
                    <Input
                      className="h-9 text-xs font-semibold"
                      value={field.state.value ?? "Tờ"}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            {/* Repeated Defect Tally Table */}
            <form.Subscribe
              selector={(s: any) => ({
                processStage: s.values.processStage,
                source: s.values.detectedAt,
                lotSize: s.values.lotSize,
                sampleSize: s.values.checkedQty || s.values.sampleSize,
              })}
              children={(vals: any) => (
                <StageDefectSelectorTable
                  processStage={
                    vals.processStage ||
                    (vals.source === "IQC"
                      ? "IQC"
                      : vals.source === "FQC"
                        ? "FQC"
                        : "PRINTING")
                  }
                  lotSize={Number(vals.lotSize) || 0}
                  sampleSize={Number(vals.sampleSize) || 0}
                  defectDetails={defectDetails}
                  onChange={(nextItems, summary) => {
                    setDefectDetails(nextItems);
                    if (summary.ngSampleQty > 0) {
                      form.setFieldValue("defectQty", summary.ngSampleQty);
                      const checked = Number(vals.sampleSize) || 0;
                      if (checked > 0) {
                        form.setFieldValue("okQty", Math.max(0, checked - summary.ngSampleQty));
                      }
                    }
                  }}
                />
              )}
            />

            {/* Issue Summary (12-Col Anti-Truncation Textarea per Rule 6) */}
            <div className="pt-2">
              <form.Field name="issueSummary">
                {(field: any) => (
                  <F
                    field={field}
                    label="D2. Mô tả lỗi"
                    required
                  >
                    <Textarea
                      rows={2}
                      className="min-h-[64px] text-sm"
                      value={field.state.value ?? ""}
                      placeholder="Mô tả rõ lỗi gì, ở vị trí nào trên tờ in/hộp, tỷ lệ lỗi bao nhiêu %..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            {/* Prefilled Failed Criteria Table from QC Checklist */}
            {failedCriteria.length > 0 && (
              <div className="p-3 bg-rose-50/70 border border-rose-200 rounded-lg space-y-2">
                <div className="text-xs font-bold text-rose-900 flex items-center gap-1.5">
                  <FileWarning className="w-4 h-4 text-rose-600" />
                  Chỉ tiêu Không đạt từ phiếu kiểm ({failedCriteria.length}):
                </div>
                <div className="overflow-x-auto bg-white rounded border border-rose-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-rose-100/60 text-rose-900 text-left">
                        <th className="py-1.5 px-2.5">Tên chỉ tiêu</th>
                        <th className="py-1.5 px-2.5">Tiêu chuẩn</th>
                        <th className="py-1.5 px-2.5 text-right">Giá trị đo</th>
                        <th className="py-1.5 px-2.5">Ghi chú lỗi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rose-100">
                      {failedCriteria.map((fc: any, idx: number) => (
                        <tr key={fc.id || idx}>
                          <td className="py-1.5 px-2.5 font-semibold text-slate-900">
                            {fc.name}
                          </td>
                          <td className="py-1.5 px-2.5 font-mono text-slate-600">
                            {fc.standard ||
                              `${fc.minValue ?? "—"}..${fc.maxValue ?? "—"} ${fc.unit || ""}`}
                          </td>
                          <td className="py-1.5 px-2.5 text-right font-mono font-bold text-rose-700">
                            {fc.measuredValue ?? "FAIL"} {fc.unit || ""}
                          </td>
                          <td className="py-1.5 px-2.5 text-slate-600">
                            {fc.note || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ═══ CARD 2: QUYẾT ĐỊNH XỬ LÝ (ISO 9001 §8.7 DISPOSITION) & CHI PHÍ COPQ ═══ */}
        <Card className="border-none shadow-2xs ring-1 ring-slate-200">
          <CardHeader className="bg-amber-50/60 py-2.5 border-b border-amber-200/80">
            <CardTitle className="text-sm font-black uppercase tracking-wider text-amber-950 flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-amber-600" /> Hướng xử lý &amp; chi phí
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3.5">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {dispositionOptions.map((opt: any) => {
                const isSelected = selectedDisposition === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => form.setFieldValue("disposition", opt.value)}
                    className={cn(
                      "text-left px-3 py-2 rounded-lg border transition-all flex items-center",
                      isSelected
                        ? `${opt.color || "border-indigo-300 bg-indigo-50 text-indigo-900"} ring-2 ring-indigo-600 font-bold shadow-xs`
                        : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                    )}
                  >
                    <div className="text-xs font-bold leading-snug">
                      {opt.label}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
              <form.Field name="responsibleDept">
                {(field: any) => (
                  <F field={field} label="Bộ phận chịu trách nhiệm">
                    <ComboboxConfigMaster
                      categoryCode="USER_SECTION"
                      value={field.state.value}
                      onChange={field.handleChange}
                      placeholder="Chọn bộ phận phụ trách..."
                      hideLabel
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="ownerId">
                {(field: any) => (
                  <F field={field} label="Người phụ trách">
                    <SharedCombobox
                      type="user"
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn nhân sự phụ trách..."
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="costImpact">
                {(field: any) => (
                  <F
                    field={field}
                    label="Chi phí lỗi (đ)"
                  >
                    <NumericInput
                      className="h-9 font-mono font-bold text-rose-700 bg-rose-50/40 border-rose-200"
                      value={field.state.value ?? 0}
                      placeholder="0"
                      onChange={(rawVal) => field.handleChange(Number(rawVal) || 0)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            {/* Bóc tách Chi phí xử lý lỗi (COPQ Cost Breakdown: Scrap + Rework + Reprint) */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-3 rounded-lg bg-amber-50/50 border border-amber-200/80">
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold text-rose-800">
                  1. Hủy hàng (đ)
                </Label>
                <NumericInput
                  className="h-8 font-mono text-xs bg-white"
                  value={scrapCost}
                  onChange={(rawVal) => {
                    const next = Math.max(0, Number(rawVal) || 0);
                    setScrapCost(next);
                    form.setFieldValue("costImpact", next + reworkCost + reprintCost);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold text-amber-900">
                  2. Lựa / sửa lỗi (đ)
                </Label>
                <NumericInput
                  className="h-8 font-mono text-xs bg-white"
                  value={reworkCost}
                  onChange={(rawVal) => {
                    const next = Math.max(0, Number(rawVal) || 0);
                    setReworkCost(next);
                    form.setFieldValue("costImpact", scrapCost + next + reprintCost);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold text-indigo-900">
                  3. In bù / phụ phí (đ)
                </Label>
                <NumericInput
                  className="h-8 font-mono text-xs bg-white"
                  value={reprintCost}
                  onChange={(rawVal) => {
                    const next = Math.max(0, Number(rawVal) || 0);
                    setReprintCost(next);
                    form.setFieldValue("costImpact", scrapCost + reworkCost + next);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold text-slate-700">
                  Ghi chú hạch toán chi phí xử lý
                </Label>
                <Input
                  className="h-8 text-xs bg-white"
                  value={costNotes}
                  onChange={(e) => setCostNotes(e.target.value)}
                  placeholder="VD: Trừ công nợ NCC / Tính vào CP xưởng..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══ CARD 3: PHÂN TÍCH NGUYÊN NHÂN GỐC RỄ 6M (ISHIKAWA) & 5-WHY (D4) ═══ */}
        <Card className="border-none shadow-2xs ring-1 ring-slate-200">
          <CardHeader className="bg-indigo-50/60 py-2.5 border-b border-indigo-200/80">
            <CardTitle className="text-sm font-black uppercase tracking-wider text-indigo-950 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-indigo-600" /> D4. Nguyên nhân gốc
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3.5">
            <div>
              <Label className="text-xs font-bold text-slate-700 mb-1.5 block">
                Nhóm nguyên nhân 6M (Ishikawa):
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {ISHIKAWA_6M.map((m) => {
                  const active = selected6M === m.code;
                  return (
                    <button
                      key={m.code}
                      type="button"
                      onClick={() =>
                        form.setFieldValue("defectCategory6M", m.code)
                      }
                      className={cn(
                        "px-3 py-2 rounded-lg border text-left transition-all",
                        active
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div className="text-xs font-bold">{m.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 5-Why Step-by-Step Analysis */}
            <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2.5">
              <div className="text-xs font-bold text-slate-800">
                5 lần hỏi "Tại sao?":
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <div className="flex items-center gap-2">
                  <Badge className="bg-rose-600 text-white font-mono w-16 justify-center shrink-0">
                    Tại sao 1?
                  </Badge>
                  <Input
                    value={why1}
                    onChange={(e) => setWhy1(e.target.value)}
                    placeholder="Tại sao lỗi này xảy ra trực tiếp trên máy/tờ in?"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-orange-600 text-white font-mono w-16 justify-center shrink-0">
                    Tại sao 2?
                  </Badge>
                  <Input
                    value={why2}
                    onChange={(e) => setWhy2(e.target.value)}
                    placeholder="Tại sao nguyên nhân ở Why 1 lại phát sinh?"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-600 text-white font-mono w-16 justify-center shrink-0">
                    Tại sao 3?
                  </Badge>
                  <Input
                    value={why3}
                    onChange={(e) => setWhy3(e.target.value)}
                    placeholder="Tại sao thông số/điều kiện ở Why 2 bị lệch?"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-indigo-600 text-white font-mono w-16 justify-center shrink-0">
                    Tại sao 4?
                  </Badge>
                  <Input
                    value={why4}
                    onChange={(e) => setWhy4(e.target.value)}
                    placeholder="Tại sao quy trình kiểm soát không phát hiện sớm?"
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <Badge className="bg-emerald-700 text-white font-mono w-16 justify-center shrink-0">
                    Tại sao 5?
                  </Badge>
                  <Input
                    value={why5}
                    onChange={(e) => setWhy5(e.target.value)}
                    placeholder="Nguyên nhân gốc rễ về hệ thống / SOP / đào tạo là gì?"
                    className="h-8 text-xs bg-white"
                  />
                </div>
              </div>
            </div>

            <form.Field name="rootCause">
              {(field: any) => (
                <F
                  field={field}
                  label="Nguyên nhân gốc"
                >
                  <Textarea
                    rows={2}
                    className="min-h-[60px] text-sm"
                    value={field.state.value ?? ""}
                    placeholder="Tổng hợp nguyên nhân cốt lõi (Nếu để trống, hệ thống tự động nối chuỗi 5-Why ở trên)..."
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </F>
              )}
            </form.Field>
          </CardContent>
        </Card>

        {/* ═══ CARD 4: HÀNH ĐỘNG NGĂN CHẶN (D3), KHẮC PHỤC (D5) & PHÒNG NGỪA (D7) ═══ */}
        <Card className="border-none shadow-2xs ring-1 ring-slate-200">
          <CardHeader className="bg-emerald-50/60 py-3 border-b border-emerald-200/80">
            <CardTitle className="text-sm font-black uppercase tracking-wider text-emerald-950 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-emerald-600" /> D3–D7. Ngăn chặn, khắc phục, phòng ngừa
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <form.Field name="containmentAction">
                {(field: any) => (
                  <F
                    field={field}
                    label="D3. Ngăn chặn"
                  >
                    <Textarea
                      rows={3}
                      className="text-xs"
                      value={field.state.value ?? ""}
                      placeholder="VD: Dán tem ĐỎ cách ly 2 pallet bán thành phẩm tại khu vực Bế; dừng máy kiểm tra dao..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="correctiveAction">
                {(field: any) => (
                  <F
                    field={field}
                    label="D5. Khắc phục"
                  >
                    <Textarea
                      rows={3}
                      className="text-xs"
                      value={field.state.value ?? ""}
                      placeholder="VD: Thay mới lưỡi dao bế + cân chỉnh lại áp lực bàn bế; chạy thử 50 tờ KCS ký duyệt..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="preventiveAction">
                {(field: any) => (
                  <F
                    field={field}
                    label="D7. Phòng ngừa"
                  >
                    <Textarea
                      rows={3}
                      className="text-xs"
                      value={field.state.value ?? ""}
                      placeholder="VD: Cập nhật giới hạn số lượt dập khuôn tối đa (100,000 lượt) vào lịch bảo trì khuôn..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <form.Field name="resolutionResult">
                {(field: any) => (
                  <F field={field} label="D6/D8. Kết quả xử lý">
                    <Textarea
                      rows={2}
                      className="text-xs border-emerald-200 bg-emerald-50/30"
                      value={field.state.value ?? ""}
                      placeholder="VD: Đã lựa xong 1,000 hộp, cứu được 820 hộp ĐẠT nhập kho, hủy 180 hộp NG..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>

              <form.Field name="capaPlan">
                {(field: any) => (
                  <F field={field} label="Kế hoạch & thời hạn">
                    <Textarea
                      rows={2}
                      className="text-xs"
                      value={field.state.value ?? ""}
                      placeholder="Ghi chú tiến độ thực hiện và người nghiệm thu đóng phiếu NCR..."
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </F>
                )}
              </form.Field>
            </div>
          </CardContent>
        </Card>

        {/* ═══ CARD 5: HÌNH ẢNH HIỆN TRẠNG LỖI & HÌNH ẢNH KẾT QUẢ XỬ LÝ ═══ */}
        <QcCategorizedMediaSection
          mode="NCR"
          value={attachments}
          onChange={setAttachments}
        />

        {/* Bottom Submit Bar (Dual-Position Submit per Rule 3.2) */}
        <div className="sticky bottom-0 z-40 p-4 bg-white/95 backdrop-blur-md border border-slate-200 rounded-lg flex justify-end gap-3 shadow-sm">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="min-w-[100px]"
          >
            Huỷ
          </Button>
          <form.Subscribe selector={(s: any) => [s.canSubmit, s.isSubmitting]}>
            {([canSubmit, submitting]: any) => (
              <Button
                type="submit"
                disabled={!canSubmit || submitting || isSubmitting}
                className="min-w-[160px] bg-rose-600 hover:bg-rose-700 text-white font-bold"
              >
                {submitting || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Đang lưu...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> {submitLabel}
                  </>
                )}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  );
}
